/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Car,
  Coffee,
  Dumbbell,
  Edit,
  Loader2,
  MoreHorizontal,
  Plus,
  Shield,
  Sparkles,
  Trash2,
  Tv,
  Waves,
  Wifi,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { amenitiesActions } from '../../actions/amenities.action';
import { cn } from '../../lib/utils';
import { Amenity, AmenityFormData } from '../../types/amenities/amenities';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useToast } from '../ui/use-toast';
import { createAdminNotification } from '../../services/notificationService';
import AmenityForm from './amenity-form';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Technology: Wifi,
  Transportation: Car,
  Health: Dumbbell,
  Recreation: Waves,
  Dining: Coffee,
  Security: Shield,
  Convenience: Sparkles,
  Entertainment: Tv,
};

const categoryColors: Record<string, string> = {
  Technology: 'bg-info/20 text-info',
  Transportation: 'bg-warning/20 text-warning',
  Health: 'bg-success/20 text-success',
  Recreation: 'bg-primary/20 text-primary',
  Dining: 'bg-destructive/20 text-destructive',
  Security: 'bg-muted text-muted-foreground',
  Convenience: 'bg-primary/20 text-primary',
  Entertainment: 'bg-info/20 text-info',
};

// Helper function to determine category and icon based on amenity name
const getAmenityCategoryAndIcon = (name: string) => {
  const lowerName = name.toLowerCase();

  if (
    lowerName.includes('wifi') ||
    lowerName.includes('internet') ||
    lowerName.includes('high-speed')
  ) {
    return { category: 'Technology', icon: Wifi };
  }
  if (lowerName.includes('parking') || lowerName.includes('car') || lowerName.includes('garage')) {
    return { category: 'Transportation', icon: Car };
  }
  if (
    lowerName.includes('fitness') ||
    lowerName.includes('gym') ||
    lowerName.includes('dumbbell')
  ) {
    return { category: 'Health', icon: Dumbbell };
  }
  if (lowerName.includes('pool') || lowerName.includes('swimming') || lowerName.includes('waves')) {
    return { category: 'Recreation', icon: Waves };
  }
  if (
    lowerName.includes('cafe') ||
    lowerName.includes('restaurant') ||
    lowerName.includes('coffee') ||
    lowerName.includes('dining')
  ) {
    return { category: 'Dining', icon: Coffee };
  }
  if (
    lowerName.includes('security') ||
    lowerName.includes('shield') ||
    lowerName.includes('24/7')
  ) {
    return { category: 'Security', icon: Shield };
  }
  if (lowerName.includes('tv') || lowerName.includes('entertainment')) {
    return { category: 'Entertainment', icon: Tv };
  }

  return { category: 'Convenience', icon: Sparkles };
};

const AmenitiesManagement = () => {
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchAmenities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await amenitiesActions.getAmenities();
      setAmenities(response.data || []);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch amenities';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAmenities();
  }, [fetchAmenities]);

  const handleAddAmenity = () => {
    setEditingAmenity(null);
    setShowForm(true);
  };

  const handleEditAmenity = (amenity: Amenity) => {
    setEditingAmenity(amenity);
    setShowForm(true);
  };

  const handleDeleteAmenity = async (id: string) => {
    setDeleteConfirmId(id);
    const amenity = amenities.find((a) => a._id === id);
    setDeleteName(amenity?.name || null);
  };

  const confirmDeleteAmenity = async () => {
    if (!deleteConfirmId) return;
    try {
      setDeleteLoading(deleteConfirmId);
      const deletedName = amenities.find((a) => a._id === deleteConfirmId)?.name || 'Amenity';
      await amenitiesActions.deleteAmenity(deleteConfirmId);
      toast({
        title: 'Success',
        description: 'Amenity deleted successfully',
      });
      createAdminNotification({
        type: 'alert',
        title: 'Amenity deleted',
        message: `"${deletedName}" was removed from amenities.`,
        priority: 'low',
      });
      setDeleteConfirmId(null);
      setDeleteName(null);
      fetchAmenities();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete amenity',
        variant: 'destructive',
      });
    } finally {
      setDeleteLoading(null);
    }
  };

  const cancelDeleteAmenity = () => {
    setDeleteConfirmId(null);
    setDeleteName(null);
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      setToggleLoading(id);
      const amenity = amenities.find((a) => a._id === id);
      await amenitiesActions.updateAmenity(id, { isActive: !isActive });
      toast({
        title: 'Success',
        description: `Amenity ${!isActive ? 'activated' : 'deactivated'} successfully`,
      });
      createAdminNotification({
        type: 'alert',
        title: !isActive ? 'Amenity activated' : 'Amenity deactivated',
        message: `"${amenity?.name || id}" was ${!isActive ? 'activated' : 'deactivated'}.`,
        priority: 'low',
      });
      fetchAmenities();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update amenity status',
        variant: 'destructive',
      });
    } finally {
      setToggleLoading(null);
    }
  };

  const handleFormSubmit = async (data: AmenityFormData) => {
    try {
      setFormLoading(true);
      if (editingAmenity) {
        await amenitiesActions.updateAmenity(editingAmenity._id, data);
        toast({
          title: 'Success',
          description: 'Amenity updated successfully',
        });
        createAdminNotification({
          type: 'alert',
          title: 'Amenity updated',
          message: `"${data.name || editingAmenity.name}" was updated.`,
          priority: 'low',
        });
      } else {
        await amenitiesActions.addAmenity(data);
        toast({
          title: 'Success',
          description: 'Amenity added successfully',
        });
        createAdminNotification({
          type: 'alert',
          title: 'Amenity created',
          message: `"${data.name}" was added to amenities.`,
          priority: 'low',
        });
      }
      setShowForm(false);
      fetchAmenities();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save amenity',
        variant: 'destructive',
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAmenity(null);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }
  return (
    <div className='space-y-6'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-foreground'>Amenities</h1>
          <p className='text-muted-foreground'>
            Manage property amenities and features. {amenities.length} total amenities.
          </p>
        </div>
        <Button className='gap-2' onClick={handleAddAmenity}>
          <Plus className='w-4 h-4' />
          Add Amenity
        </Button>
      </div>

      {/* Amenities Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
        {amenities.map((amenity) => {
          const { category, icon: IconComponent } = getAmenityCategoryAndIcon(amenity.name);
          const isInactive = amenity.isActive === false;
          return (
            <div
              key={amenity._id}
              className='glass-card rounded-xl p-8 transition-all duration-300 hover:border-primary/30 group cursor-pointer min-h-[180px]'
            >
              <div className='flex items-start justify-between mb-4'>
                <div
                  className={cn(
                    'flex items-center justify-center w-14 h-14 rounded-xl transition-transform group-hover:scale-110',
                    categoryColors[category] || 'bg-muted text-muted-foreground'
                  )}
                >
                  <IconComponent className='w-6 h-6' />
                </div>
                <div className='flex items-center gap-2'>
                  <span
                    className={cn(
                      'text-xs px-2 py-1 rounded',
                      isInactive
                        ? 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                        : 'bg-green-200 text-green-800 dark:bg-[#095D52] dark:text-[#e9e6c9]'
                    )}
                  >
                    {isInactive ? 'Inactive' : 'Active'}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='ghost'
                        size='iconSm'
                        className='opacity-0 group-hover:opacity-100 transition-opacity'
                      >
                        <MoreHorizontal className='w-4 h-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleEditAmenity(amenity)}>
                        <Edit className='w-4 h-4 mr-2' />
                        Edit Amenity
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className='text-destructive'
                        onClick={() => handleDeleteAmenity(amenity._id)}
                        disabled={deleteLoading === amenity._id}
                      >
                        {deleteLoading === amenity._id ? (
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        ) : (
                          <Trash2 className='w-4 h-4 mr-2' />
                        )}
                        Delete Amenity
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <h3 className='font-semibold text-lg text-foreground mb-3 group-hover:text-primary transition-colors'>
                {amenity.name}
              </h3>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-muted-foreground bg-muted px-2 py-1 rounded'>
                  {category}
                </span>
                <span className='text-xs text-muted-foreground'>Slug: {amenity.slugText}</span>
              </div>
            </div>
          );
        })}
      </div>

      {amenities.length === 0 && (
        <div className='text-center py-12'>
          <p className='text-muted-foreground mb-4'>No amenities configured</p>
          <Button onClick={handleAddAmenity}>Add First Amenity</Button>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className='max-w-md bg-white border-gray-200 dark:bg-[#0b222c] dark:border-[#095D52]'>
          <DialogHeader>
            <DialogTitle className='text-gray-900 dark:text-[#e9e6c9]'>
              {editingAmenity ? 'Edit Amenity' : 'Add New Amenity'}
            </DialogTitle>
          </DialogHeader>
          <AmenityForm
            amenity={editingAmenity || undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            loading={formLoading}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={cancelDeleteAmenity}>
        <DialogContent className='max-w-md bg-white border-gray-200 dark:bg-[#0b222c] dark:border-[#095D52]'>
          <DialogHeader>
            <DialogTitle className='text-gray-900 dark:text-[#e9e6c9]'>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className='py-4 text-gray-700 dark:text-[#e9e6c9]'>
            Are you sure you want to delete the amenity{' '}
            <span className='font-semibold text-gray-900 dark:text-[#e9e6c9]'>{deleteName}</span>?
          </div>
          <div className='flex justify-end gap-2'>
            <Button
              variant='outline'
              onClick={cancelDeleteAmenity}
              disabled={deleteLoading === deleteConfirmId}
              className='border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-[#095D52] dark:text-[#e9e6c9] dark:hover:bg-[#095D52]'
            >
              No
            </Button>
            <Button
              variant='destructive'
              onClick={confirmDeleteAmenity}
              disabled={deleteLoading === deleteConfirmId}
              className='bg-red-600 text-white hover:bg-red-700 dark:bg-red-800 dark:hover:bg-red-900'
            >
              {deleteLoading === deleteConfirmId ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                'Yes'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AmenitiesManagement;
